import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

function TermsModal() {
  const location = useLocation();
  const navigate = useNavigate();
  const isOpen = location.pathname === '/terms';
  const isEmbedded = window.self !== window.top; // Check if in iframe
  
  const searchParams = new URLSearchParams(location.search);
  const returnUrl = searchParams.get('returnUrl');
  
  const onClose = () => {
    // If in iframe, use postMessage to communicate with parent
    if (isEmbedded) {
      window.parent.postMessage({ type: 'TERMS_MODAL_CLOSED' }, '*');
    } else if (returnUrl) {
      // If returnUrl is provided, navigate there
      window.location.href = returnUrl;
    } else {
      // Otherwise use regular navigation
      navigate(-1);
    }
  };
  
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  
  // Listen for messages from parent (Clerk) if in iframe
  useEffect(() => {
    const handleMessage = (event) => {
      // Verify origin for security if needed
      // if (event.origin !== 'https://accounts.boxyqr.com') return;
      
      if (event.data?.type === 'CLOSE_MODAL') {
        onClose();
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  
  if (!isOpen && !isEmbedded) return null;
  
  // Adjust styling based on whether it's embedded or not
  const containerStyle = isEmbedded ? {
    position: 'relative',
    height: '100vh',
    width: '100%',
    overflow: 'auto',
    background: 'white'
  } : {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '1rem'
  };
  
  return (    
    <div style={containerStyle}>
      <div className={isEmbedded ? "w-full h-full" : "bg-white rounded-lg p-6 max-w-4xl w-full mx-4 relative max-h-[90vh] overflow-y-auto"}>
        {!isEmbedded && (
          <button 
            onClick={onClose} 
            className="absolute top-4 right-4 text-xl font-bold hover:text-gray-700"
            aria-label="Close"
          >
            ×
          </button>
        )}
        
        <h2 className="text-2xl font-bold mb-6">Terms of Service</h2>
        
        <div className="prose prose-sm max-w-none">
          <h3 className="text-xl font-semibold mt-4 mb-2">Last Updated: April 21, 2025</h3>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">1. Introduction</h3>
          <p>
            Welcome to BoxyQR. These Terms of Service ("Terms") govern your access to and use of BoxyQR's website, applications, and services (collectively, the "Services"). By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Services.
          </p>
          <p>
            BoxyQR ("we," "us," or "our") reserves the right to modify these Terms at any time. We will provide notice of any material changes by posting the updated Terms on our website or through other communications. Your continued use of the Services after such modifications constitutes your acceptance of the revised Terms.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">2. Services Description</h3>
          <p>
            BoxyQR provides QR code generation, management, and analytics services. Our Services may include free and paid subscription plans with varying features and limitations as described on our website or within the application.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">3. Account Registration and Security</h3>
          <p>
            To use certain features of our Services, you must register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
          </p>
          <p>
            You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
          </p>
          
          <h4 className="text-md font-semibold mt-3 mb-1">3.3 Age Restriction</h4>
          <p>
            By creating an account and using our Services, you represent and warrant that you are at least 16 years of age. If you are under 16 years of age, you may not use our Services. We reserve the right to request proof of age at any time, and may terminate your account if we discover you are under 16 years of age.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">4. User Conduct</h3>
          <p>
            You agree not to use the Services in any way that violates any applicable law or regulation. You agree not to impersonate any person or entity or falsely state or misrepresent your affiliation with a person or entity.
          </p>
          <p>
            You will not engage in any activity that interferes with or disrupts the Services or attempt to gain unauthorized access to the Services or related systems or networks.
          </p>
          
          <h4 className="text-md font-semibold mt-3 mb-1">4.3 Prohibited Content and Activities</h4>
          <p>
            You agree not to use the Services to create, distribute, or promote:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>Content that contains harassment, hate speech, threats, or obscene materials</li>
            <li>QR codes or links that lead to illegal, harmful, or explicit content</li>
            <li>QR codes or links intended to deceive, scam, or harm others</li>
            <li>Content that infringes on intellectual property rights or the privacy of others</li>
          </ul>
          <p>
            We reserve the right to remove any content and suspend or terminate the accounts of users who violate these prohibitions. We may report illegal activities to appropriate law enforcement authorities.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">5. Subscription Plans and Payment</h3>
          <p>
            We offer various subscription plans, including a free plan with limited features and paid plans with additional features. Detailed information about our subscription plans, including price and features, is available on our website.
          </p>
          
          <h4 className="text-md font-semibold mt-3 mb-1">5.1 Free Trial</h4>
          <p>
            We may offer a free trial of our paid subscription plans. After the trial period expires, your account will automatically be downgraded to the free plan unless you choose to subscribe to a paid plan.
          </p>
          
          <h4 className="text-md font-semibold mt-3 mb-1">5.2 Billing and Renewal</h4>
          <p>
            For paid plans, you agree to pay all fees in accordance with the pricing and payment terms in effect at the time of your subscription or renewal. Unless you notify us before the end of the current subscription period that you want to cancel, your subscription will automatically renew.
          </p>
          
          <h4 className="text-md font-semibold mt-3 mb-1">5.3 Price Changes</h4>
          <p>
            We reserve the right to change our prices at any time. If we change the fees for your subscription, we will give you notice of the change before your next renewal. Your continued use of the Services after the price change becomes effective constitutes your agreement to pay the updated price.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">6. QR Code and Link Limitations</h3>
          <h4 className="text-md font-semibold mt-3 mb-1">6.1 Free Plan Limitations</h4>
          <p>
            On the free plan, QR codes and shortened links will remain active for 14 days from creation. After this period, they will automatically become inactive.
          </p>
          
          <h4 className="text-md font-semibold mt-3 mb-1">6.2 Quantity Limitations</h4>
          <p>
            The free plan allows for creation of a maximum of 5 QR codes and 10 shortened links per account. Paid plans offer increased or unlimited quantities as specified in the plan details.
          </p>
          
          <h4 className="text-md font-semibold mt-3 mb-1">6.3 Data Deletion</h4>
          <p>
            We reserve the right to delete any and all data associated with an account at any time if the account is not in good standing, including but not limited to: accounts with unpaid balances, accounts in violation of these Terms, inactive accounts, or accounts that we determine, in our sole discretion, are being used in a manner inconsistent with the intended use of the Services.
          </p>
          <p>
            For free accounts, we may delete data after 14 days of inactivity. For paid accounts, if payment is not received by the due date, we reserve the right to immediately delete all data associated with the account without prior notice.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">7. Intellectual Property</h3>
          <p>
            The Services and all content, features, and functionality thereof are owned by BoxyQR, its licensors, or other providers and are protected by copyright, trademark, patent, trade secret, and other intellectual property laws.
          </p>
          <p>
            You retain all rights to any content you create, upload, or store through our Services. By using our Services, you grant us a non-exclusive, worldwide, royalty-free license to use, reproduce, modify, and display your content solely for the purpose of providing the Services to you.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">8. Termination</h3>
          <h4 className="text-md font-semibold mt-3 mb-1">8.1 Termination by You</h4>
          <p>
            You may terminate your account at any time by following the instructions on our website or by contacting us. Upon termination, your account will be deactivated, and you will lose access to all content associated with your account.
          </p>
          
          <h4 className="text-md font-semibold mt-3 mb-1">8.2 Termination by Us</h4>
          <p>
            We reserve the right to suspend or terminate your access to the Services at any time and for any reason, including but not limited to: violation of these Terms, use of the Services in a manner that may cause harm to us or others, failure to pay any fees when due, inactive accounts, or at our sole discretion, with or without cause.
          </p>
          
          <h4 className="text-md font-semibold mt-3 mb-1">8.3 Effect of Termination</h4>
          <p>
            Upon termination of your account, you will lose access to your account and all associated content. If on a paid plan, you will not be entitled to a refund of any prepaid fees. All QR codes and shortened links created by you will be deactivated.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">9. Data Privacy and Security</h3>
          <p>
            Your use of the Services is subject to our Privacy Policy, which is incorporated into these Terms by reference. The Privacy Policy explains how we collect, use, and protect your personal information. By using our Services, you consent to the data practices described in the Privacy Policy.
          </p>
          <p>
            We implement reasonable security measures to protect your data. However, no method of transmission over the Internet or electronic storage is 100% secure. Therefore, while we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">10. User Content and Acceptable Use</h3>
          <p>
            You may not use our Services to create, upload, or distribute content that is illegal, harmful, threatening, abusive, defamatory, or otherwise objectionable. We reserve the right to remove or disable access to any content that violates these Terms.
          </p>
          <p>
            We reserve the right, but have no obligation, to monitor and review content created using our Services. You are solely responsible for all content you create, upload, or distribute through the Services.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">11. Indemnification</h3>
          <p>
            You agree to defend, indemnify, and hold harmless BoxyQR, its affiliates, licensors, and service providers from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees arising out of or relating to your violation of these Terms or your use of the Services.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">12. Dispute Resolution and Waiver of Legal Action</h3>
          <p>
            These Terms shall be governed by the laws of the State of California. Any dispute arising from or relating to these Terms shall be resolved exclusively by binding arbitration in San Francisco, California, rather than in courts of law.
          </p>
          <p>
            To the maximum extent permitted by applicable law, you expressly waive your right to bring any legal action in court against BoxyQR relating to your use of the Services or these Terms. You agree that all disputes shall be resolved exclusively through binding arbitration.
          </p>
          <p>
            Any arbitration shall be conducted on an individual basis and not as a class action. You expressly waive any right to bring or participate in a class proceeding. You agree that regardless of any statute or law, any claim must be filed within one (1) year after such claim arose or be forever barred.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">13. Third-Party Services</h3>
          <p>
            The Services may contain links to third-party websites or services that are not owned or controlled by BoxyQR. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">14. Service Level Agreement</h3>
          <p>
            We will use commercially reasonable efforts to make the Services available 24 hours a day, 7 days a week, except for planned downtime. Support services will be provided based on your subscription plan.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">15. Feedback and Suggestions</h3>
          <p>
            If you provide any feedback or suggestions regarding the Services, you grant BoxyQR a perpetual, worldwide, royalty-free license to use such feedback for any purpose without compensation to you.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">16. Force Majeure</h3>
          <p>
            BoxyQR shall not be liable for any delay or failure to perform resulting from causes outside its reasonable control, including acts of God, natural disasters, pandemic, war, terrorism, riot, government actions, or unavailability of third-party service providers.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">17. Export Controls</h3>
          <p>
            You agree to comply with all applicable export control laws and regulations. You represent that you are not located in a country embargoed by the United States or restricted by export controls.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">18. Warranty Disclaimer</h3>
          <p>
            THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT THE SERVICES ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">19. Limitation of Liability</h3>
          <p>
            IN NO EVENT WILL WE BE LIABLE FOR DAMAGES OF ANY KIND, UNDER ANY LEGAL THEORY, ARISING OUT OF OR IN CONNECTION WITH YOUR USE, OR INABILITY TO USE, THE SERVICES, INCLUDING ANY DIRECT, INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">20. Severability</h3>
          <p>
            If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that the Terms will otherwise remain in full force and effect.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">21. Entire Agreement</h3>
          <p>
            These Terms constitute the entire agreement between you and BoxyQR regarding the Services and supersede all prior agreements and understandings.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">22. Updates and Notifications</h3>
          <p>
            We may update these Terms of Service from time to time. We will notify you of any changes by:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>Sending an email to the address associated with your account</li>
            <li>Posting a notice on our website</li>
            <li>Displaying a notification within the application</li>
          </ul>
          <p>
            The date at the top of this document indicates when it was last updated. Your continued use of the Services after such modifications constitutes your acceptance of the revised Terms.
          </p>
          
          <h3 className="text-lg font-semibold mt-4 mb-2">Contact Us</h3>
          <p>
            If you have any questions about these Terms, please contact us at:
          </p>
          <p>
            Email: support@boxyqr.com<br />
            BoxyQR<br />
            San Francisco, CA<br />
            United States
          </p>
        </div>
      </div>
    </div>
  );
}

export default TermsModal;